/**
 * Decodes HTML entities commonly found in attribute values.
 */
function decodeHTMLEntities(str: string): string {
  return str
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');
}

/**
 * Encodes characters that are unsafe in HTML double-quoted attribute values.
 */
function encodeHTMLAttr(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;');
}

/**
 * Extracts a data attribute value from an HTML tag string.
 */
function extractDataAttr(tag: string, attr: string): string {
  const regex = new RegExp(`data-${attr}="([^"]*)"`, 'i');
  const match = tag.match(regex);
  return match ? decodeHTMLEntities(match[1]) : '';
}

/**
 * Splits tables at Handlebars block boundaries so that blocks wrap complete
 * sub-tables instead of being invalid children of <tbody>.
 *
 * Handles: {{#block}} and {{/block}} between </tr> and <tr> or at
 * table body boundaries.
 *
 * @example
 * // Input:  <table><tbody><tr><td>a</td></tr>{{#Items}}<tr><td>b</td></tr>{{/Items}}</tbody></table>
 * // Output: <table><tbody><tr><td>a</td></tr></tbody></table>{{#Items}}<table><tbody><tr><td>b</td></tr></tbody></table>{{/Items}}
 */
function splitTablesAtBlockBoundaries(html: string): string {
  let result = html;

  // Pattern: block tag(s) between two rows — split the table through the tag(s)
  // Matches: </tr> (whitespace) (one or more block tags) (whitespace) <tr>
  // We process iteratively since block tags might be adjacent (e.g., {{/inner}}{{#outer}})
  const betweenRowsRegex = /(<\/tr>\s*)((\{\{[#/][^}]*\}\}\s*)+)(\s*<tr[\s>])/gi;
  result = result.replace(betweenRowsRegex, (_match, closeTr, blockTags, _lastTag, openTr) => {
    return `${closeTr}</tbody></table>${blockTags}<table><tbody>${openTr}`;
  });

  // Pattern: block tag(s) right after <tbody> opening (block wraps from start of table body)
  // <table><tbody> (block tags) <tr> → (block tags) <table><tbody><tr>
  const startOfBodyRegex = /(<table[^>]*>\s*<tbody[^>]*>\s*)((\{\{#[^}]*\}\}\s*)+)(\s*<tr[\s>])/gi;
  result = result.replace(startOfBodyRegex, (_match, _tableOpen, blockTags, _lastTag, openTr) => {
    return `${blockTags}<table><tbody>${openTr}`;
  });

  // Pattern: block tag(s) right before </tbody> closing (block wraps to end of table body)
  // </tr> (block tags) </tbody></table> → </tr></tbody></table> (block tags)
  const endOfBodyRegex = /(<\/tr>\s*)((\{\{\/[^}]*\}\}\s*)+)\s*<\/tbody>\s*<\/table>/gi;
  result = result.replace(endOfBodyRegex, (_match, closeTr, blockTags) => {
    return `${closeTr}</tbody></table>${blockTags}`;
  });

  // Handle tables without explicit <tbody> (Tiptap often renders <table><tr>...</tr></table>)
  const betweenRowsNoTbodyRegex = /(<\/tr>\s*)((\{\{[#/][^}]*\}\}\s*)+)(\s*<tr[\s>])/gi;
  result = result.replace(betweenRowsNoTbodyRegex, (_match, closeTr, blockTags, _lastTag, openTr) => {
    // Only apply if we're inside a table without tbody (check for </tbody> not already present)
    if (closeTr.includes('</tbody>')) return _match;
    return `${closeTr}</table>${blockTags}<table>${openTr}`;
  });

  // Clean up double-wrapped: </tbody></table></tbody></table> → </tbody></table>
  result = result.replace(/<\/tbody>\s*<\/table>\s*<\/tbody>\s*<\/table>/g, '</tbody></table>');
  // Clean up: <table><tbody><table><tbody> → <table><tbody>
  result = result.replace(/<table><tbody>\s*<table><tbody>/g, '<table><tbody>');

  return result;
}

/**
 * Merges adjacent tables that are separated only by Handlebars block tags.
 * This is the reverse of splitTablesAtBlockBoundaries.
 *
 * @example
 * // Input:  </tbody></table>{{#Items}}<table><tbody>
 * // Output: {{#Items}}
 */
function mergeTablesAroundBlockTags(html: string): string {
  let result = html;
  let maxIterations = 100;

  // 1. Merge middle: </tbody></table> (block tags) <table><tbody> → (block tags)
  const mergeWithTbodyRegex = /<\/tbody>\s*<\/table>\s*((\{\{[#/][^}]*\}\}\s*)+)\s*<table[^>]*>\s*<tbody[^>]*>/gi;
  while (mergeWithTbodyRegex.test(result) && maxIterations-- > 0) {
    result = result.replace(mergeWithTbodyRegex, '$1');
  }

  // 2. Merge trailing: </tr> </tbody></table> ({{/...}} tags) → </tr> ({{/...}} tags) </tbody></table>
  //    Block-end tags that were split out of a table end get moved back inside
  result = result.replace(
    /(<\/tr>)\s*(<\/tbody>\s*<\/table>)\s*((\{\{\/[^}]*\}\}\s*)+)/gi,
    (_match, closeTr, tableClose, blockTags) => `${closeTr}${blockTags}${tableClose}`
  );

  // 3. Merge leading: ({{#...}} tags) <table><tbody> <tr> → <table><tbody> ({{#...}} tags) <tr>
  //    Block-start tags that were split out of a table start get moved back inside
  result = result.replace(
    /((\{\{#[^}]*\}\}\s*)+)\s*(<table[^>]*>\s*<tbody[^>]*>)\s*(<tr[\s>])/gi,
    (_match, blockTags, _last, tableOpen, openTr) => `${tableOpen}${blockTags}${openTr}`
  );

  // 4. Merge without tbody: </table> (block tags) <table> → (block tags)
  const mergeNoTbodyRegex = /<\/table>\s*((\{\{[#/][^}]*\}\}\s*)+)\s*<table[^>]*>/gi;
  maxIterations = 100;
  while (mergeNoTbodyRegex.test(result) && maxIterations-- > 0) {
    result = result.replace(mergeNoTbodyRegex, '$1');
  }

  return result;
}

/**
 * Converts editor HTML (with span/div elements) to clean Handlebars template HTML.
 * - Wrapping block divs are converted to {{#blockType variable}}...{{/blockType}} syntax
 * - Legacy block start/end spans are also handled (backward compat)
 * - Inline Handlebars nodes are converted to {{variable}} syntax
 * - Tiptap-specific data attributes are stripped (data-color, data-type, etc.)
 * - Standard HTML and inline styles are preserved
 *
 * @example
 * // Input:  <p>Hello <span data-handlebars data-variable="user.name">User name</span>!</p>
 * // Output: <p>Hello {{user.name}}!</p>
 *
 * @example
 * // Input:  <div data-handlebars-block data-block-type="if" data-variable="condition"><p>content</p></div>
 * // Output: {{#if condition}}<p>content</p>{{/if}}
 *
 * @example
 * // Input:  <mark data-color="#fef08a" style="background-color: #fef08a">text</mark>
 * // Output: <mark style="background-color: #fef08a">text</mark>
 */
export function convertToTemplate(html: string): string {
  let result = html;

  // 1. Convert wrapping block divs (new format) — process innermost first iteratively
  // Match a div[data-handlebars-block] that doesn't contain another div[data-handlebars-block]
  const wrappingBlockRegex = /<div\s+[^>]*data-handlebars-block[^>]*>((?:(?!<div\s+[^>]*data-handlebars-block).)*?)<\/div>/s;
  let maxIterations = 100; // Safety limit for deeply nested blocks
  while (wrappingBlockRegex.test(result) && maxIterations-- > 0) {
    result = result.replace(wrappingBlockRegex, (match, innerHTML) => {
      const blockType = extractDataAttr(match, 'block-type');
      const variable = extractDataAttr(match, 'variable');
      const openTag = variable ? `{{#${blockType} ${variable}}}` : `{{#${blockType}}}`;
      const closeTag = `{{/${blockType}}}`;
      return `${openTag}${innerHTML}${closeTag}`;
    });
  }

  // 1b. Strip empty gap paragraphs between adjacent block tags.
  //     These are editor-only artifacts inserted by BlockGapEnforcer to allow
  //     the user to place their cursor between adjacent blocks.
  result = result.replace(
    /(\{\{\/[^}]+\}\})(\s*<p>\s*(?:<br\s*\/?>)?\s*<\/p>\s*)+(\{\{#)/g,
    '$1$3'
  );

  // 2. Legacy: Convert block start span nodes (backward compat)
  result = result.replace(
    /<span[^>]*data-handlebars-block-start[^>]*data-block-type="([^"]*)"[^>]*data-variable="([^"]*)"[^>]*>.*?<\/span>/g,
    (_match, blockType, variable) => {
      const v = decodeHTMLEntities(variable);
      return v ? `{{#${decodeHTMLEntities(blockType)} ${v}}}` : `{{#${decodeHTMLEntities(blockType)}}}`;
    }
  );
  result = result.replace(
    /<span[^>]*data-handlebars-block-start[^>]*data-variable="([^"]*)"[^>]*data-block-type="([^"]*)"[^>]*>.*?<\/span>/g,
    (_match, variable, blockType) => {
      const v = decodeHTMLEntities(variable);
      return v ? `{{#${decodeHTMLEntities(blockType)} ${v}}}` : `{{#${decodeHTMLEntities(blockType)}}}`;
    }
  );

  // 3. Legacy: Convert block end span nodes (backward compat)
  result = result.replace(
    /<span[^>]*data-handlebars-block-end[^>]*data-block-type="([^"]*)"[^>]*>.*?<\/span>/g,
    (_match, blockType) => `{{/${decodeHTMLEntities(blockType)}}}`
  );

  // 4. Convert inline Handlebars nodes to {{variable}} syntax
  result = result.replace(
    /<span[^>]*data-handlebars[^>]*data-variable="([^"]*)"[^>]*>.*?<\/span>/g,
    (_match, variable) => `{{${decodeHTMLEntities(variable)}}}`
  );

  // 5. Strip Tiptap-specific data attributes while preserving the elements
  result = result.replace(/\s*data-[a-z-]+="[^"]*"/gi, '');
  result = result.replace(/\s*data-[a-z-]+(?=[\s>])/gi, '');

  // 6. Merge adjacent tables separated by block tags back into single tables
  //    (reverse of the split done in convertFromTemplate)
  result = mergeTablesAroundBlockTags(result);

  return result;
}

/**
 * Generates a unique block ID for template conversion.
 */
function generateBlockId(): string {
  return `block-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Converts clean Handlebars template HTML back to editor HTML with wrapping divs.
 * This is useful for loading existing templates into the editor.
 *
 * @example
 * // Input:  <p>Hello {{user.name}}!</p>
 * // Output: <p>Hello <span data-handlebars data-variable="user.name" data-valid="true">{{user.name}}</span>!</p>
 *
 * @example
 * // Input:  {{#if condition}}<p>content</p>{{/if}}
 * // Output: <div data-handlebars-block data-block-type="if" data-variable="condition" data-block-id="..." data-valid="true"><p>content</p></div>
 */
export function convertFromTemplate(templateHtml: string): string {
  let result = templateHtml;

  // 1. Split tables at block boundaries so blocks wrap complete sub-tables
  //    instead of being invalid children of <tbody>
  result = splitTablesAtBlockBoundaries(result);

  // 2. Convert block pairs to wrapping divs — process innermost first iteratively
  // Match an opening tag, content (no nested opening tags), and its matching closing tag
  const blockPairRegex = /\{\{#([a-zA-Z][a-zA-Z0-9.:-]*)(?:\s+([^}]*))?\}\}((?:(?!\{\{#).)*?)\{\{\/\1\}\}/s;
  let maxIterations = 100;
  while (blockPairRegex.test(result) && maxIterations-- > 0) {
    result = result.replace(blockPairRegex, (_match, blockType, variable, content) => {
      const blockId = generateBlockId();
      const trimmedVar = encodeHTMLAttr((variable || '').trim());
      return `<div data-handlebars-block data-block-type="${blockType}" data-variable="${trimmedVar}" data-block-id="${blockId}" data-valid="true">${content}</div>`;
    });
  }

  // 2b. Insert gap paragraphs between adjacent handlebarsBlock divs so the editor
  //     has editable positions between blocks on initial load.
  result = result.replace(
    /(<\/div>)\s*(<div\s[^>]*data-handlebars-block)/g,
    '$1<p></p>$2'
  );

  // 3. Convert inline variables (but not block syntax which we already handled)
  // Match {{variable}} but not {{#...}} or {{/...}}
  result = result.replace(
    /\{\{(?![#/])([^}]+)\}\}/g,
    (_match, variable) => {
      const encoded = encodeHTMLAttr(variable);
      return `<span data-handlebars data-variable="${encoded}" data-valid="true">{{${variable}}}</span>`;
    }
  );

  return result;
}

/**
 * Migrates old bookmark-style block content (paired start/end spans) to wrapping divs.
 * Used when loading legacy editor HTML content.
 */
export function migrateBookmarkToWrapping(html: string): string {
  if (!html.includes('data-handlebars-block-start')) {
    return html;
  }

  // Convert old editor HTML to clean template syntax first
  const template = convertToTemplate(html);
  // Then convert back with new wrapping format
  return convertFromTemplate(template);
}
